import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { BackButton } from '../components/BackButton.js';
import { InvoiceDocument } from '../components/Invoice.js';
import { Button, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';

/** One invoice of the customer, printable; an open order can be cancelled (T-48). */
export function InvoicePage() {
  const { invoiceId = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const timeZone = useMe().data?.account.timezone;
  const [confirming, setConfirming] = useState(false);
  const invoice = useQuery({
    queryKey: ['billing', 'invoice', invoiceId],
    queryFn: () =>
      unwrap(api.GET('/billing/invoices/{invoiceId}', { params: { path: { invoiceId } } })),
  });
  const cancel = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/billing/invoices/{invoiceId}/cancel', { params: { path: { invoiceId } } })),
    onSuccess: (data) => {
      client.setQueryData(['billing', 'invoice', invoiceId], data);
      void client.invalidateQueries({ queryKey: ['billing'], exact: true });
      void client.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      setConfirming(false);
    },
  });
  if (invoice.isPending) return <Loading />;
  if (invoice.isError)
    return (
      <LoadError message={errorMessage(invoice.error, t)} onRetry={() => void invoice.refetch()} />
    );
  const inv = invoice.data;
  const cancellable = inv.status === 'open' && inv.method === 'bank_transfer';
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <BackButton to="/billing">{t('billing.title')}</BackButton>
        <Button onClick={() => window.print()}>{t('invoice.print')}</Button>
      </div>
      {params.get('ordered') && inv.status === 'open' ? (
        <div className="print:hidden">
          <Notice kind="success">{t('invoice.ordered')}</Notice>
        </div>
      ) : null}
      <InvoiceDocument invoice={inv} timeZone={timeZone} />
      {cancellable ? (
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {confirming ? (
            <>
              <p className="font-semibold">{t('invoice.cancelConfirm')}</p>
              <Button variant="danger" busy={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('invoice.cancelYes')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('app.cancel')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('invoice.cancel')}
            </Button>
          )}
        </div>
      ) : null}
      {cancel.isError ? <Notice kind="error">{errorMessage(cancel.error, t)}</Notice> : null}
    </div>
  );
}
